import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

type Pricing = { title?: string; totalSteps: number; sortOrder?: number; price: number };
type DiscountTier = { _id?: string; title?: string; minTotalDeposited: number; discountPercent: number };

const AdminOrderPricingPage: React.FC = () => {
    const [pricing, setPricing] = useState<Pricing[]>([]);
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [stepDrafts, setStepDrafts] = useState<Record<number, string>>({});
    const [titleDrafts, setTitleDrafts] = useState<Record<number, string>>({});
    const [sortOrderDrafts, setSortOrderDrafts] = useState<Record<number, string>>({});
    const [newTitle, setNewTitle] = useState('');
    const [newSteps, setNewSteps] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newSortOrder, setNewSortOrder] = useState('');
    const [discountTiers, setDiscountTiers] = useState<DiscountTier[]>([]);
    const [discountDrafts, setDiscountDrafts] = useState<Record<number, string>>({});
    const [minDepositedDrafts, setMinDepositedDrafts] = useState<Record<number, string>>({});
    const [discountTitleDrafts, setDiscountTitleDrafts] = useState<Record<number, string>>({});
    const [newMinDeposited, setNewMinDeposited] = useState('');
    const [newDiscountPercent, setNewDiscountPercent] = useState('');
    const [newDiscountTitle, setNewDiscountTitle] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const loadPricing = async () => {
        try {
            const data = await apiService.get<Pricing[]>('/orders/pricing');
            setPricing(data || []);
            setDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, String(item.price)])));
            setStepDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, String(item.totalSteps)])));
            setTitleDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, item.title || ''])));
            setSortOrderDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, item.sortOrder == null ? '' : String(item.sortOrder)])));
        } catch {
            setError('Không tải được bảng giá.');
        }
    };

    const loadDiscountTiers = async () => {
        try {
            const data = await apiService.get<DiscountTier[]>('/transactions/discount-tiers');
            setDiscountTiers(data || []);
            setDiscountDrafts(Object.fromEntries((data || []).map((item) => [item.minTotalDeposited, String(item.discountPercent)])));
            setMinDepositedDrafts(Object.fromEntries((data || []).map((item) => [item.minTotalDeposited, String(item.minTotalDeposited)])));
            setDiscountTitleDrafts(Object.fromEntries((data || []).map((item, index) => [item.minTotalDeposited, item.title || `VIP ${index + 1}`])));
        } catch {
            setError('Không tải được cấu hình giảm giá.');
        }
    };

    useEffect(() => {
        void loadPricing();
        void loadDiscountTiers();
    }, []);

    const savePrice = async (currentSteps: number, steps: number, title: string, value: string, sortOrder: string) => {
        const price = Number(value);
        if (!Number.isInteger(steps) || steps < 1) {
            setError('Số lần phải là số nguyên dương.');
            return;
        }
        if (!Number.isFinite(price) || price < 0) {
            setError('Giá phải là số không âm.');
            return;
        }
        try {
            const parsedSortOrder = sortOrder.trim() ? Number(sortOrder) : null;
            if (parsedSortOrder !== null && (!Number.isInteger(parsedSortOrder) || parsedSortOrder < 1)) {
                setError('STT phải là số nguyên dương.');
                return;
            }
            await apiService.post(`/orders/pricing/${currentSteps}`, { totalSteps: steps, title: title.trim(), price, sortOrder: parsedSortOrder });
            setMessage(title.trim() ? `Đã lưu ${title.trim()} (${steps} lần).` : `Đã lưu giá cho ${steps} lần.`);
            setError('');
            await loadPricing();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không lưu được giá.');
        }
    };

    const addPrice = async () => {
        const steps = Number(newSteps);
        if (!Number.isInteger(steps) || steps < 1) {
            setError('Số lần phải là số nguyên dương.');
            return;
        }
        if (newSortOrder && (!Number.isInteger(Number(newSortOrder)) || Number(newSortOrder) < 1)) {
            setError('STT phải là số nguyên dương.');
            return;
        }
        await savePrice(steps, steps, newTitle, newPrice, newSortOrder);
        setNewTitle('');
        setNewSteps('');
        setNewPrice('');
        setNewSortOrder('');
    };

    const deletePrice = async (item: Pricing) => {
        if (!window.confirm(`Xóa giá gói ${item.title || `${item.totalSteps} lần`}?`)) return;
        try {
            await apiService.delete(`/orders/pricing/${item.totalSteps}`);
            setMessage(`Đã xóa giá gói ${item.title || `${item.totalSteps} lần`}.`);
            setError('');
            await loadPricing();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không xóa được giá.');
        }
    };

    const saveDiscountTier = async (previousMinTotalDeposited: number, minTotalDeposited: number, title: string, value: string) => {
        if (!Number.isFinite(minTotalDeposited) || minTotalDeposited < 0) {
            setError('Mốc tổng nạp phải là số không âm.');
            return;
        }
        const discountPercent = Number(value);
        if (!title.trim()) {
            setError('Tên khuyến mãi không được để trống.');
            return;
        }
        if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
            setError('Phần trăm giảm phải từ 0 đến 100.');
            return;
        }
        try {
            await apiService.post('/transactions/discount-tiers', { title: title.trim(), previousMinTotalDeposited, minTotalDeposited, discountPercent });
            setMessage(`Đã lưu ${title.trim()} (${discountPercent}%) từ ${minTotalDeposited.toLocaleString('vi-VN')} đ tổng nạp.`);
            setError('');
            await loadDiscountTiers();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không lưu được cấu hình giảm giá.');
        }
    };

    const addDiscountTier = async () => {
        const minTotalDeposited = Number(newMinDeposited);
        if (!newDiscountTitle.trim()) {
            setError('Vui lòng nhập tên khuyến mãi.');
            return;
        }
        if (!Number.isFinite(minTotalDeposited) || minTotalDeposited < 0) {
            setError('Mốc tổng nạp phải là số không âm.');
            return;
        }
        await saveDiscountTier(minTotalDeposited, minTotalDeposited, newDiscountTitle, newDiscountPercent);
        setNewDiscountTitle('');
        setNewMinDeposited('');
        setNewDiscountPercent('');
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-3xl space-y-5">
                <div><p className="text-sm font-medium text-blue-600">Cấu hình mã chung sức</p><h2 className="mt-1 text-2xl font-semibold text-gray-900">Giá theo số lần</h2><p className="mt-1 text-sm text-gray-500">Thành viên sẽ bị trừ tiền khi tạo mã chung sức; đối tác được ghi nhận công nợ.</p></div>
                {(message || error) && <div className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{error || message}</div>}
                <section className="rounded-2xl bg-white p-5 shadow-sm">
                    <div className="hidden grid-cols-[64px_minmax(130px,1fr)_90px_minmax(120px,1fr)_auto_auto] gap-3 border-b border-gray-100 pb-3 text-xs font-semibold uppercase text-gray-500 sm:grid"><span>STT</span><span>Tên gói</span><span>Số lần</span><span>Giá (đ)</span><span /><span /></div>
                    <div className="divide-y divide-gray-100">
                        {pricing.map((item) => (
                            <div key={item.totalSteps} className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-2 py-3 sm:grid-cols-[64px_minmax(130px,1fr)_90px_minmax(120px,1fr)_auto_auto] sm:gap-3">
                                <input
                                    aria-label="STT gói giá (không bắt buộc)"
                                    value={sortOrderDrafts[item.totalSteps] || ''}
                                    onChange={(event) => setSortOrderDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))}
                                    className="min-w-0 rounded-lg border border-gray-200 px-3 py-2"
                                    placeholder="STT"
                                    inputMode="numeric"
                                />
                                <input aria-label="Tên gói giá" value={titleDrafts[item.totalSteps] || ''} onChange={(event) => setTitleDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" placeholder="Tên gói (không bắt buộc)" />
                                <input aria-label="Số lần" value={stepDrafts[item.totalSteps] || ''} onChange={(event) => setStepDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))} className="col-span-2 min-w-0 rounded-lg border border-gray-200 px-3 py-2 sm:col-span-1" inputMode="numeric" />
                                <input aria-label="Giá gói" value={drafts[item.totalSteps] || ''} onChange={(event) => setDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))} className="col-span-2 min-w-0 rounded-lg border border-gray-200 px-3 py-2 sm:col-span-1" inputMode="numeric" />
                                <button type="button" onClick={() => void savePrice(item.totalSteps, Number(stepDrafts[item.totalSteps]), titleDrafts[item.totalSteps] || '', drafts[item.totalSteps] || '0', sortOrderDrafts[item.totalSteps] || '')} className="col-span-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 sm:col-span-1">Lưu</button>
                                <button type="button" onClick={() => void deletePrice(item)} className="col-span-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 sm:col-span-1">Xóa</button>
                            </div>
                        ))}
                    </div>
                    {pricing.length === 0 && <p className="py-5 text-sm text-gray-500">Chưa có bảng giá.</p>}
                </section>
                <section className="rounded-2xl bg-white p-5 shadow-sm"><h3 className="font-semibold">Thêm giá mới</h3><div className="mt-3 flex flex-col gap-3 sm:flex-row"><input value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="Tên riêng (không bắt buộc)" className="min-w-0 rounded-lg border px-3 py-2 sm:flex-1" /><input value={newSteps} onChange={(event) => setNewSteps(event.target.value.replace(/\D/g, ''))} placeholder="Số lần" className="rounded-lg border px-3 py-2 sm:w-32" inputMode="numeric" /><input value={newPrice} onChange={(event) => setNewPrice(event.target.value.replace(/\D/g, ''))} placeholder="Giá (đ)" className="rounded-lg border px-3 py-2 sm:w-36" inputMode="numeric" /><input value={newSortOrder} onChange={(event) => setNewSortOrder(event.target.value.replace(/\D/g, ''))} placeholder="STT (không bắt buộc)" aria-label="STT gói giá mới (không bắt buộc)" className="rounded-lg border px-3 py-2 sm:w-40" inputMode="numeric" /><button type="button" onClick={() => void addPrice()} className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700">Thêm giá</button></div></section>
                <section className="rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="font-semibold">Khuyến mãi theo tổng tiền đã nạp</h3>
                        <p className="mt-1 text-sm text-gray-500">Đặt tên cho từng hạng. Hệ thống áp dụng hạng cao nhất đạt được; người dùng được cộng tiền vào ví, đối tác được giảm tiền thanh toán mã chung sức.</p>
                    </div>
                    <div className="mt-4 hidden grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_auto] gap-3 border-b border-gray-100 pb-3 text-xs font-semibold uppercase text-gray-500 sm:grid">
                        <span>Tên hạng</span><span>Tổng nạp từ (đ)</span><span>Tỷ lệ (%)</span><span />
                    </div>
                    <div className="divide-y divide-gray-100">
                        {discountTiers.map((item) => (
                            <div key={item.minTotalDeposited} className="grid grid-cols-1 items-center gap-2 py-3 sm:grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_auto] sm:gap-3">
                                <input aria-label="Tên hạng khuyến mãi" value={discountTitleDrafts[item.minTotalDeposited] || ''} onChange={(event) => setDiscountTitleDrafts((current) => ({ ...current, [item.minTotalDeposited]: event.target.value }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" placeholder="Ví dụ: VIP 1" />
                                <input aria-label="Mốc tổng nạp" value={minDepositedDrafts[item.minTotalDeposited] || ''} onChange={(event) => setMinDepositedDrafts((current) => ({ ...current, [item.minTotalDeposited]: event.target.value.replace(/\D/g, '') }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" inputMode="numeric" />
                                <input
                                    aria-label="Tỷ lệ khuyến mãi"
                                    value={discountDrafts[item.minTotalDeposited] || ''}
                                    onChange={(event) => setDiscountDrafts((current) => ({ ...current, [item.minTotalDeposited]: event.target.value.replace(/[^0-9.]/g, '') }))}
                                    className="rounded-lg border border-gray-200 px-3 py-2"
                                    inputMode="decimal"
                                />
                                <button type="button" onClick={() => void saveDiscountTier(item.minTotalDeposited, Number(minDepositedDrafts[item.minTotalDeposited]), discountTitleDrafts[item.minTotalDeposited] || '', discountDrafts[item.minTotalDeposited] || '0')} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">Lưu</button>
                            </div>
                        ))}
                    </div>
                    {discountTiers.length === 0 && <p className="py-4 text-sm text-gray-500">Chưa có hạng khuyến mãi.</p>}
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <input value={newDiscountTitle} onChange={(event) => setNewDiscountTitle(event.target.value)} placeholder="Tên hạng (ví dụ: VIP 1)" className="min-w-0 rounded-lg border px-3 py-2 sm:flex-1" />
                        <input value={newMinDeposited} onChange={(event) => setNewMinDeposited(event.target.value.replace(/\D/g, ''))} placeholder="Tổng nạp từ (đ)" className="rounded-lg border px-3 py-2 sm:flex-1" inputMode="numeric" />
                        <input value={newDiscountPercent} onChange={(event) => setNewDiscountPercent(event.target.value.replace(/[^0-9.]/g, ''))} placeholder="Tỷ lệ (%)" className="rounded-lg border px-3 py-2 sm:w-32" inputMode="decimal" />
                        <button type="button" onClick={() => void addDiscountTier()} className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700">Thêm hạng</button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminOrderPricingPage;
